import type { Marker } from "@googlemaps/markerclusterer";
import type { Action, CallToAction, Icon, StatusMessageModule, TextualDisplay } from "@ebay/experience-types-ebay";
import type { Locality } from "@ebay/locale-ebay";
import type WatchService from "@ebay-business-components/favorites-utils/dist/services/watch-service-provider";
import type { LocalizedContent } from "@ebay-business-components/favorites-utils/dist/interface";
import type {
  Banner,
  BubbleHelp,
  ItemConditionPillData,
  MapParamsGatheringModule,
  MapViewExperienceResponse,
  MapViewModule,
  PointOfInterest,
  PostalCodePillData,
  PricePillData,
  WalkingDistance,
  WatchlistPillData,
} from "../../@types/mapviewxs";
import type { Input as MarkerInput } from "../map-marker/index.marko";
import type { UserLocationMarkerInput, UserLocationMarkerState } from "../map-user-location-marker/types";
import type {
  FavoritesSettingsType,
  LayoutType,
  ListingsViewType,
} from "../../pages/mapview,localpickup,kostenloseabholung,localcollection,remiseenmainspropres,ritiroamano/+handler";
import type { PreviewCardPosition } from "../../utils/preview-card-position";

export interface Input {
  watchListItems?: WatchListEntries;
  apiKey: string;
  mapId: string;
  model?: MapViewModules;
  waitingForService?: boolean;
  isPreviewCardEnabled?: boolean;
  isMobile?: boolean;
  layout?: LayoutType;
  listingsView?: ListingsViewType;
  isRecommendationsView?: boolean;
  entryPageId?: string;
  csrfToken?: string;
  marketplaceId?: string;
  favoritesSettings?: FavoritesSettingsType;
  locality?: Locality;
  dialogTitle?: string;
  dialogSubtitle?: string;
  watchInstance?: WatchService | null;
  signInPageUrl?: string;
}

export interface State {
  highlightedItem: number | null;
  prevHighlightedItem: number | null;
  selectedItems: number[];
  prevSelectedItems: number[];
  clusterPointsOfInterest: Array<PointOfInterest>;
  shouldTriggerLoadMore: boolean;
  isLoading: boolean;
  isLoadingMoreItems: boolean;
  isLeftRailItemHover: boolean;
  showErrorMessage: boolean;
  leftRailItems: PointOfInterest[];
  pointsOfInterest: PointOfInterest[];
  filteredPointsOfInterest: PointOfInterest[];
  resetMapviewButton?: CallToAction;
  mapMessage?: StatusMessageModule;
  help?: BubbleHelp;
  survey?: BubbleHelp;
  surveyButton?: CallToAction;
  itemCardControls: ItemCardControls;
  isPreviewCardEnabled: boolean;
  mapAccessibilityTexts: MapAccessibilityTexts;
  isEmptyMapTrackerVisible: boolean;
  isAutoLoadTrackerVisible: boolean;
  windowWidth: number;
  walkingDistance: WalkingDistance;
  locateMe?: Icon;
  userLocationCoordinates: LatLng | null;
  showLocateMeButton: boolean;
  shouldOpenMapHelp?: boolean;
  locality?: Locality;
  watchListItems: WatchListEntries;
  userLocationZipcode?: string;
  isLocationChangeDialogOpen: boolean;
  isFilterPriceDialogOpen: boolean;
  isFilterConditionDialogOpen: boolean;
  postalCodePillData?: PostalCodePillData;
  watchlistPillData?: WatchlistPillData;
  itemConditionPillData?: ItemConditionPillData;
  pricePillData?: PricePillData;
  meta?: MapViewModule["meta"];
  availableFilters: AvailableFilter[];
  expandedView: View;
  isWatchlistPillActive: boolean;
  showPostcodeInquiry: boolean;
  banners: Banner[];
  controlButtons?: { zoomIn?: Icon; zoomOut?: Icon };
}

export type AvailableFilterOption = {
  fieldId: string;
  label: string;
  isEnabled: boolean;
};

export type FilterKey = "condition" | "price";

export type AvailableFilter = {
  label: string;
  filterKey: FilterKey;
  // For condition filter
  options?: AvailableFilterOption[];
  // For price filter
  minValue?: number;
  maxValue?: number;
};

export type MarkerWithCircle = {
  marker: Marker;
  component: Marko.Component<MarkerInput>;
  bounds?: google.maps.LatLngBounds;
  circle?: google.maps.Circle;
  originalIndex?: number;
};

export type ItemCardControls = {
  itemCardNavigationAction?: CallToAction;
  itemCardCloseAction?: Action;
  sellerListingCountLabel?: TextualDisplay;
};

export type UserLocationMarker = {
  marker: Marker;
  component: Marko.Component<UserLocationMarkerInput, UserLocationMarkerState>;
};

export type LoadLocationDataParams = {
  zipcode: string;
  distance: string;
  sources?: string;
};

export type MarkerLibrary = google.maps.MarkerLibrary;
export type Map = google.maps.Map;
export type LatLng = google.maps.LatLng;
export type LatLngLiteral = google.maps.LatLngLiteral;
export type LatLngBounds = google.maps.LatLngBounds;
export type MapAccessibilityTexts = MapViewModule["mapAccessibilityTexts"];

export type MarkerSelectionState = "unselected" | "selected" | "highlighted";
export type MarkerWatchState = "watched" | "unwatched";

export type MarkerContentKey = "multipleListingMarker" | "doubleListingMarker" | "singleListingMarker" | "clusterListings";
export type MapViewModules = MapViewExperienceResponse["modules"] & {
  mapParamsGathering?: Partial<MapParamsGatheringModule>;
};
export enum MessageName {
  MapView = "map-view",
  MapViewInitialized = "map-view-initialized",
  MapViewLocationChange = "map-view-location-change",
  SetListings = "map-view-set-listings",
  OpenLocationChangeDialog = "map-view-open-location-change-dialog",
}
export type MapContext = {
  locality?: Locality;
  localizedContent?: LocalizedContent;
  watchInstance?: WatchService | null;
  handleWatchListItemToggle?: (listingid: string) => void;
  watchListItems?: WatchListEntries;
};

export type WatchListEntries =
  | {
      item: {
        listing: {
          listingId: string;
          poi?: PointOfInterest;
        };
      };
    }[]
  | [];

export type WatchListItemsResponse = {
  data: {
    currentUser: {
      watchingDetails: {
        defaultWatchList: {
          watchListEntries: {
            entries: WatchListEntries;
          };
        };
      };
    };
  };
};
export interface MarkerRendererOptions {
  points: PointOfInterest[];
  selectionState: MarkerSelectionState;
  watchlistState?: MarkerWatchState;
  handlePreviewClose?: () => void;
  defaultPreviewPosition?: PreviewCardPosition;
  itemCardControls?: ItemCardControls;
  isPreviewCardEnabled?: boolean;
  mapAccessibilityTexts?: MapAccessibilityTexts;
  locality?: Locality;
  localizedContent?: LocalizedContent;
  watchInstance?: WatchService | null;
  watchListItems?: WatchListEntries;
  handleWatchListItemToggle?: (listingid: string) => void;
}
export interface RenderMarkerOptions {
  map: google.maps.Map;
  markerLibrary: MarkerLibrary;
  points: PointOfInterest[];
  selectionState: MarkerSelectionState;
  watchlistState?: MarkerWatchState;
  isPreviewCardEnabled?: boolean;
  handlePreviewClose?: () => void;
  defaultPreviewPosition?: PreviewCardPosition;
  itemCardControls?: ItemCardControls;
  mapAccessibilityTexts?: MapAccessibilityTexts;
  locality?: Locality;
  localizedContent?: LocalizedContent;
  watchInstance?: WatchService | null;
  watchListItems?: WatchListEntries;
  handleWatchListItemToggle?: (listingid: string) => void;
  position?: LatLngLiteral;
}

export interface FindMarkerIndexParams {
  points: MarkerWithCircle[];
  itemIndex: number;
  listingId?: string;
  hasFilters: boolean;
}

export type View = "map" | "list";

export enum MapDialog {
  LocationChange = "locationChange",
  FilterPrice = "filterPrice",
  FilterCondition = "filterCondition",
}
